import React from 'react'
import { useToast } from '../components/shared/toast/toast.hooks'

/**
 * @typedef {Object} NotificationData
 * @property {string} title - Notification title
 * @property {string} body - Notification body/message
 * @property {Object} [data] - Additional data (optional)
 * @property {string} [data.url] - URL to open when clicked (optional)
 * @property {Object} [notification] - FCM notification object (optional)
 * @property {string} [notification.title] - FCM notification title (optional)
 * @property {string} [notification.body] - FCM notification body (optional)
 * @property {string} [message] - Alternative message field (optional)
 */

/**
 * Show a notification using the toast component
 * @param {NotificationData} notificationData - The notification data from FCM
 * @param {Object} toastHook - The useToast hook result
 */
export const showNotification = (notificationData, toastHook) => {
  if (!toastHook) {
    console.warn('⚠️ Toast hook not provided.')
    return
  }

  const { openToast } = toastHook

  // Extract data from notification - FCM data structure
  const notification = notificationData.notification || {}
  const data = notificationData.data || {}

  const title = notification.title || notificationData.title || 'Notification'
  const body = notification.body || notificationData.body || notificationData.message || ''
  const url = data.url || data.link

  // Create action if there's a URL
  const action = url
    ? {
        label: 'View',
        url,
      }
    : null

  // Show toast notification
  openToast({
    severity: 'info',
    title,
    description: body,
    action,
  })
}

/**
 * Hook to handle FCM notifications
 * Call this in your main app component
 */
export const useNotificationService = () => {
  const toastHook = useToast()

  const handleNotification = React.useCallback(
    (notificationData) => {
      showNotification(notificationData, toastHook)
    },
    [toastHook]
  )

  return { showNotification: handleNotification }
}
